
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "@/hooks/useProject";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface DeleteLaunchDialogProps {
  launch: {
    id: number;
    jotform_form_mappings?: { form_title: string; jotform_form_id: string } | null;
    clientes?: { nombre: string } | null;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteLaunchDialog({ launch, open, onOpenChange }: DeleteLaunchDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const { projectClient, currentProject } = useProject();

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      // Primero eliminamos los códigos relacionados
      const { error: codesError } = await projectClient
        .from("form_codes")
        .delete()
        .eq("form_launch_id", launch.id)
        .eq('project_id', projectClient.getProjectId());
      
      if (codesError) throw codesError;
      
      // Luego eliminamos el lanzamiento
      const { error: launchError } = await projectClient
        .from("form_launches")
        .delete()
        .eq("id", launch.id)
        .eq('project_id', projectClient.getProjectId());
      
      if (launchError) throw launchError;
      
      toast.success("Lanzamiento eliminado correctamente");
      queryClient.invalidateQueries({ queryKey: ["launches", currentProject] });
      queryClient.invalidateQueries({ queryKey: ["form_codes", currentProject] });
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Eliminar Lanzamiento</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p>
            ¿Estás seguro de que deseas eliminar el lanzamiento{" "}
            <span className="font-bold">
              {launch.jotform_form_mappings?.form_title ||
               launch.clientes?.nombre ||
               `#${launch.id}`}
            </span>?
          </p>
          <p className="text-destructive">
            Esta acción eliminará también todos los códigos asociados a este lanzamiento.
          </p>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isLoading}
            >
              {isLoading ? "Eliminando..." : "Eliminar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
